// Browser-side helpers for the AppDetail "tool actions" panel: format the
// React TSX source via prettier (lazy-loaded), and bundle source + data +
// a tiny README into a downloadable .zip via client-zip.

let cachedFormatter: ((src: string) => Promise<string>) | null = null;

export async function formatTsx(source: string): Promise<string> {
  if (!cachedFormatter) {
    const [{ format }, ts, estree] = await Promise.all([
      import("prettier/standalone"),
      import("prettier/plugins/typescript"),
      import("prettier/plugins/estree"),
    ]);
    cachedFormatter = (src: string) =>
      format(src, {
        parser: "typescript",
        plugins: [ts.default ?? ts, estree.default ?? estree],
        singleQuote: false,
        semi: true,
        trailingComma: "all",
        printWidth: 100,
      });
  }
  return cachedFormatter(source);
}

export async function downloadWidgetZip(opts: {
  appSlug: string;
  toolSlug: string;
  description: string | null;
  source: string;
  data: unknown;
}): Promise<void> {
  const { downloadZip } = await import("client-zip");
  const dataJson = JSON.stringify(opts.data ?? {}, null, 2);
  const readme = renderReadme(opts);
  const now = new Date();
  const files = [
    { name: "widget.tsx", lastModified: now, input: opts.source },
    { name: "widget.data.json", lastModified: now, input: dataJson },
    { name: "README.md", lastModified: now, input: readme },
  ];
  const blob = await downloadZip(files).blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${opts.appSlug}-${opts.toolSlug}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadProjectZip(opts: {
  appSlug: string;
  appName?: string | null;
  widgets: Array<{
    toolSlug: string;
    description: string | null;
    source: string;
    data: unknown;
  }>;
}): Promise<void> {
  const { downloadZip } = await import("client-zip");
  const now = new Date();
  const files: { name: string; lastModified: Date; input: string }[] = [];
  for (const w of opts.widgets) {
    files.push({ name: `${w.toolSlug}/widget.tsx`, lastModified: now, input: w.source });
    files.push({
      name: `${w.toolSlug}/widget.data.json`,
      lastModified: now,
      input: JSON.stringify(w.data ?? {}, null, 2),
    });
    files.push({
      name: `${w.toolSlug}/README.md`,
      lastModified: now,
      input: renderReadme({ appSlug: opts.appSlug, toolSlug: w.toolSlug, description: w.description }),
    });
  }
  files.push({ name: "README.md", lastModified: now, input: renderProjectReadme(opts) });
  const blob = await downloadZip(files).blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${opts.appSlug}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

function renderProjectReadme(opts: {
  appSlug: string;
  appName?: string | null;
  widgets: Array<{ toolSlug: string; description: string | null }>;
}): string {
  const list = opts.widgets
    .map((w) => `- \`${w.toolSlug}/\` — ${w.description ?? "(no description)"}`)
    .join("\n");
  return `# ${opts.appName ?? opts.appSlug}

Project export from MCPBuild — \`${opts.appSlug}\` (${opts.widgets.length} widget${opts.widgets.length === 1 ? "" : "s"}).

Each widget lives in its own folder with \`widget.tsx\` (React TSX source authored
by the LLM), \`widget.data.json\` (initial \`useToolOutput()\` data), and a per-widget
\`README.md\`.

## Widgets

${list}

## Re-render

Pass any widget's \`widget.tsx\` + \`widget.data.json\` back through the \`dynamic_widget\`
MCP tool to render it inline in Claude or ChatGPT. Widgets can import \`react\`,
\`react-dom/client\`, and \`@martzmakes/widget\`.
`;
}

function renderReadme(opts: {
  appSlug: string;
  toolSlug: string;
  description: string | null;
}): string {
  return `# ${opts.toolSlug}

${opts.description ?? ""}

**App**: \`${opts.appSlug}\`
**Tool**: \`${opts.toolSlug}\`

## Files

- \`widget.tsx\` — the React TSX source authored by the LLM via the \`dynamic_widget\` / \`save_widget_to_app\` MCP tool.
- \`widget.data.json\` — the initial structured data delivered to the widget via \`useToolOutput()\`.

## Re-render

To re-render this widget inline in Claude or ChatGPT, pass the source back through \`dynamic_widget\`:

\`\`\`js
{
  source: <contents of widget.tsx>,
  data: <contents of widget.data.json>
}
\`\`\`

The widget can import \`react\`, \`react-dom/client\`, and \`@martzmakes/widget\` (which exposes \`useToolOutput\`, \`callTool\`, \`useTheme\`, \`useDisplayMode\`).
`;
}
